import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Init, Login, Identify } from './sso.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isInitialized = false;

  constructor(
    private router: Router,
    private configService: ConfigService
  ) {}

  async initialize(): Promise<void> {
    if (!this.isInitialized) {
      try {
        const ssoConfig = this.configService.getSSOConfig();
        console.log('AuthService: Initializing SSO with config:', ssoConfig);
        
        const config = await Init(ssoConfig.clientId, ssoConfig.issuer);
        console.log('AuthService: SSO Init returned config:', config);
        console.log('AuthService: SSO Config authorization_endpoint:', config?.authorization_endpoint);
        
        this.isInitialized = true;
        if (this.configService.isDebugLoggingEnabled()) {
          console.log('SSO service initialized with config:', ssoConfig);
        }
      } catch (error) {
        console.error('Failed to initialize SSO service:', error);
        throw error; // Re-throw to let calling code handle the error
      }
    }
  }

  async handleAuthentication(): Promise<boolean> {
    console.log('AuthService: handleAuthentication called');
    await this.initialize();

    // Check if there's a code parameter in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      // Exchange code for token
      try {
        console.log('AuthService: Found authentication code, attempting token exchange...');
        const result = await Identify();
        console.log('AuthService: Authentication successful:', result);
        // Clear the code parameter from URL
        this.clearUrlParams();
        return true;
      } catch (error) {
        console.error('AuthService: Failed to exchange code for token:', error);
        return false;
      }
    } else {
      // No code parameter, redirect to login
      console.log('AuthService: No authentication code found, redirecting to SSO...');
      this.redirectToSSO();
      return false; // Will redirect, so return false
    }
  }

  async redirectToSSO(): Promise<void> {
    try {
      console.log('AuthService: Initiating SSO redirect...');
      console.log('AuthService: Current URL:', window.location.href);
      
      // Ensure SSO service is initialized before redirecting
      await this.initialize();
      
      // Clear any cached authentication state
      this.clearAuthCache();
      
      console.log('AuthService: Calling SSO Login function...');
      console.log('AuthService: Redirect URI:', window.location.href);
      
      // Call the Login function with the current URL as redirect URI
      try {
        const currentUrl = window.location.origin + window.location.pathname;
        console.log('AuthService: Using current URL as redirect URI:', currentUrl);
        Login(currentUrl);
        console.log('AuthService: SSO Login function called successfully');
      } catch (loginError) {
        console.error('AuthService: Error calling SSO Login function:', loginError);
        throw loginError;
      }
      
      console.log('AuthService: SSO redirect initiated - window.location should change soon');
    } catch (error) {
      console.error('AuthService: Failed to initiate SSO redirect:', error);
    }
  }

  private clearAuthCache(): void {
    // Clear any cached tokens or authentication state
    try {
      localStorage.removeItem('pkce_state');
      localStorage.removeItem('pkce_code_verifier');
      localStorage.removeItem('redirect_uri');
      // Clear any cookies that might contain auth tokens
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      console.log('Cleared authentication cache');
    } catch (error) {
      console.error('Failed to clear auth cache:', error);
    }
  }

  private clearUrlParams(): void {
    // Remove code and state parameters from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    window.history.replaceState({}, document.title, url.pathname + url.search);
  }

  async checkAuthentication(): Promise<boolean> {
    console.log('AuthService: checkAuthentication called');
    await this.initialize();
    
    try {
      const result = await Identify();
      console.log('AuthService: User is authenticated:', result);
      return true;
    } catch (error) {
      console.log('AuthService: User is not authenticated, will redirect to SSO:', error);
      // Don't redirect here, let the calling code handle it
      return false;
    }
  }
}
